import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import ViewProductLogSheet from "../ViewProductLogSheet";

import AvatarText from "@/components/reusables/AvatarText";
import FetchWrapper from "@/components/reusables/FetchWrapper";
import CustomHeader from "@/components/reusables/Headers.tsx";
import { CustomTable } from "@/components/reusables/data-table/CustomTable";
import { Checkbox } from "@/components/ui/checkbox";
import { ROUTES } from "@/constants/routes_sidebar/routes-constant";
import { ToolsConstants } from "@/constants/tools.constant";
import DataTableSlotContextProvider from "@/context/DataTableSlot";
import { ExtendedColumnDef } from "@/interfaces/globals";
import {
  IProductLogsItem,
  ITransferRequest,
  ITransferToolItems,
} from "@/interfaces/tools.interface";
import { renderFullName } from "@/lib/utils";
import { useGetTransferToolsQuery } from "@/redux/api/toolsQuery";

const ViewTransferRequestProducts = (): JSX.Element => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [productLog, setProductLog] = React.useState<{
    isOpen: boolean;
    itemName: string;
    data: IProductLogsItem[];
  } | null>(null);

  const { data, ...rest } = useGetTransferToolsQuery(
    {
      id: id!,
    },
    {
      skip: !id,
    },
  );
  const singleData = data?.data as ITransferRequest;

  const {
    PRODUCTS,
    REQUEST: { TRANSFER_REQUEST },
  } = ToolsConstants;

  const assignedBy = singleData?.transferredBy;

  const columns: ExtendedColumnDef<ITransferToolItems>[] = [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: "id",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      ),
      meta: {
        classNameRow: "w-14",
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      map: {
        key: "Name",
        dataTransformer: (value) => value?.product?.name ?? "-",
      },
      accessorKey: "Name",
      header: "Name",
      cell: ({ row }): JSX.Element => {
        return (
          <AvatarText
            src={row?.original?.product?.images[0]}
            name={`${row.original?.product?.name}`}
            title={row.original?.product?.name}
          />
        );
      },
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Quantity",
        dataTransformer: (value) =>
          value?.transferredQuantity.toString() ?? "-",
      },
      accessorKey: "Quantity",
      header: "Quantity",
      cell: ({ row }) =>
        row.original?.transferredQuantity
          ? row.original?.transferredQuantity.toString()
          : row.original?.subProducts?.length?.toString(),
      meta: {
        minWidth: 100,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Sub Products",
        dataTransformer: (value) =>
          value?.subProducts?.length
            ? value?.subProducts?.map((item) => item.name).join(", ")
            : "-",
      },
      accessorKey: "SubProducts",
      header: "Sub Products",
      cell: ({ row }) => (
        <div className='flex flex-wrap gap-2'>
          {row.original?.subProducts?.length
            ? row.original?.subProducts?.map((item) => (
                <div key={item.id} className='bg-gray-200 rounded px-2 py-1'>
                  {item.name}
                </div>
              ))
            : "-"}
        </div>
      ),
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Assigned By",
        dataTransformer: () =>
          renderFullName({
            params: assignedBy,
          }),
      },
      accessorKey: "Assigned By",
      header: "Assigned By",
      cell: (): JSX.Element => {
        return (
          <AvatarText
            src={assignedBy?.image}
            name={`${renderFullName({
              params: assignedBy,
            })}`}
            title={renderFullName({
              params: assignedBy,
            })}
          />
        );
      },
      meta: {
        minWidth: 200,
      },
    },
  ];

  return (
    <DataTableSlotContextProvider>
      <CustomHeader
        title={PRODUCTS.HEADER}
        backBtn={() => {
          navigate(ROUTES.TOOLS + ROUTES.REQUEST);
        }}
      />
      <FetchWrapper {...rest} isTable>
        <CustomTable
          title={TRANSFER_REQUEST.TRANSFER_REQUEST_PRODUCTS}
          columns={columns}
          data={singleData?.TransferToolItems ?? []}
          empty={{}}
          functions={{
            search: TRANSFER_REQUEST.TRANSFER_REQUEST_PRODUCTS,
            isExport: true,
            columnToggle: true,
          }}
          hide={{
            filters: true,
            functions: true,
          }}
        />
        {productLog?.isOpen && (
          <ViewProductLogSheet
            toggleProductLog={() => setProductLog(null)}
            {...productLog}
          />
        )}
      </FetchWrapper>
    </DataTableSlotContextProvider>
  );
};

export default ViewTransferRequestProducts;
